import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { getMachineSafetyHistory, resolveMachineSafetyFileUrl } from "./machineSafetyApi";

const RISK_COLORS = {
  critical: { background: "#7f1d1d", color: "#fff" },
  veryHigh: { background: "#dc2626", color: "#fff" },
  high: { background: "#f97316", color: "#fff" },
  elevated: { background: "#f59e0b", color: "#111827" },
  caution: { background: "#a3e635", color: "#1f2937" },
  safe: { background: "#16a34a", color: "#fff" },
  neutral: { background: "#e5e7eb", color: "#4b5563" },
};

const HISTORY_FONT_SCOPE = {
  "&, & *": {
    fontFamily:
      '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important',
    letterSpacing: "0 !important",
  },
  "& .MuiTypography-root": {
    fontWeight: "400 !important",
  },
  "& .MuiButton-root, & .MuiChip-label, & .MuiSelect-select": {
    fontWeight: "700 !important",
    textTransform: "none !important",
  },
  "& .machine-history-important": {
    fontWeight: "800 !important",
  },
};

const statusText = (item) => {
  if (Number(item.improve_status) >= 20) return "개선 완료";
  if (item.improve_id) return "개선 중";
  if (Number(item.requires_improve) === 1) return "개선 필요";
  return "평가 완료";
};

const EvidenceImage = ({ label, src }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const hasImage = Boolean(src) && !failed;

  return (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ mb: 0.7, color: "text.secondary", fontSize: 12, fontWeight: 500 }}>
      {label}
    </Typography>
    <Box
      sx={{
        height: 116,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "#f4f6f8",
        border: "1px solid #e5e7eb",
        display: "grid",
        placeItems: "center",
      }}
    >
      {hasImage ? (
        <Box
          component="img"
          src={resolveMachineSafetyFileUrl(src)}
          alt={label}
          onError={() => setFailed(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Stack alignItems="center" spacing={0.5} sx={{ color: "#9ca3af" }}>
          <ImageNotSupportedOutlinedIcon fontSize="small" />
          <Typography sx={{ fontSize: 11 }}>등록된 사진 없음</Typography>
        </Stack>
      )}
    </Box>
  </Box>
  );
};

export default function MachineSafetyHistoryDialog({
  open,
  onClose,
  groupCode,
  projectCode,
  linkId,
  machineName,
  initialCauseId,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    getMachineSafetyHistory({ groupCode, projectCode, linkId })
      .then((result) => active && setData(result))
      .catch((requestError) => {
        if (!active) return;
        setError(
          requestError?.response?.data?.message ||
            "위험성평가 이력을 불러오지 못했습니다."
        );
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [groupCode, linkId, open, projectCode]);

  const causeItems = useMemo(() => {
    const history = data?.items || [];
    if (!initialCauseId) return history;
    return history.filter(
      (item) => String(item.register_cause_id) === String(initialCauseId)
    );
  }, [data, initialCauseId]);

  const availableMonths = useMemo(
    () => Array.from(
      new Set(
        causeItems
          .map((item) => String(item.eval_date || "").slice(0, 7))
          .filter((month) => /^\d{4}-\d{2}$/.test(month))
      )
    ).sort((a, b) => b.localeCompare(a)),
    [causeItems]
  );

  useEffect(() => {
    if (!open || availableMonths.length === 0) {
      setSelectedMonth("");
      return;
    }
    setSelectedMonth(availableMonths[0]);
  }, [availableMonths, open]);

  const items = useMemo(
    () => selectedMonth
      ? causeItems.filter((item) => String(item.eval_date || "").slice(0, 7) === selectedMonth)
      : causeItems,
    [causeItems, selectedMonth]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { ...HISTORY_FONT_SCOPE, borderRadius: fullScreen ? 0 : 2.5, maxHeight: "88vh" } }}
    >
      <DialogTitle sx={{ p: 2, pr: 6 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#eef2ff", color: "#4f46e5", display: "grid", placeItems: "center" }}>
            <HistoryIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography className="machine-history-important" sx={{ fontSize: 17, fontWeight: 700 }}>위험성평가 내역</Typography>
            <Typography noWrap sx={{ color: "text.secondary", fontSize: 12 }}>
              {data?.machine?.display_name || machineName || "설비"}
            </Typography>
          </Box>
        </Stack>
        <IconButton aria-label="닫기" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "#f7f8fb" }}>
        {loading && <Box sx={{ minHeight: 240, display: "grid", placeItems: "center" }}><CircularProgress size={30} /></Box>}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && causeItems.length > 0 && availableMonths.length > 0 && (
          <Box sx={{ mb: 1.5, px: 1.4, py: 1.15, bgcolor: "#fff", border: "1px solid #e4e7ec", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography className="machine-history-important" sx={{ fontSize: 12.5, fontWeight: 600, color: "#344054" }}>평가 월</Typography>
              <Typography sx={{ mt: 0.2, fontSize: 11.5, color: "#667085" }}>{items.length}건의 평가 이력</Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 132, flexShrink: 0 }}>
              <Select
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                inputProps={{ "aria-label": "평가 월 선택" }}
                sx={{ bgcolor: "#fff", fontSize: 13, fontWeight: 500 }}
              >
                {availableMonths.map((month) => (
                  <MenuItem key={month} value={month}>{month.replace("-", "년 ")}월</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {!loading && !error && causeItems.length === 0 && (
          <Alert severity="info">등록된 위험성평가 이력이 없습니다.</Alert>
        )}
        {!loading && !error && (
          <Stack spacing={1.25}>
            {items.map((item) => {
              const beforeStyle = RISK_COLORS[item.risk_tone] || RISK_COLORS.neutral;
              const afterTone = item.improve_after_risk >= 16 ? "critical" : item.improve_after_risk >= 13 ? "veryHigh" : item.improve_after_risk >= 9 ? "high" : item.improve_after_risk >= 7 ? "elevated" : item.improve_after_risk >= 4 ? "caution" : item.improve_after_risk >= 1 ? "safe" : "neutral";
              return (
                <Box key={item.eval_id} sx={{ bgcolor: "#fff", border: "1px solid #e4e7ec", borderRadius: 2, p: { xs: 1.4, sm: 1.8 } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={item.process_name || "공정 미지정"} variant="outlined" />
                        <Chip size="small" label={statusText(item)} color={Number(item.improve_status) >= 20 ? "success" : Number(item.requires_improve) ? "warning" : "default"} />
                      </Stack>
                      <Typography className="machine-history-important" sx={{ mt: 0.9, fontWeight: 650, lineHeight: 1.45 }}>
                        {item.danger_description}
                      </Typography>
                      <Typography sx={{ mt: 0.4, color: "text.secondary", fontSize: 12 }}>
                        평가일 {item.eval_date || "-"} · {item.danger_category || "분류 미지정"} / {item.danger_cause || "원인 미지정"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Chip size="small" label={`개선 전 ${item.risk_score ?? "-"}점`} sx={{ fontWeight: 700, ...beforeStyle }} />
                      {item.improve_after_risk != null && (
                        <Chip size="small" label={`개선 후 ${item.improve_after_risk}점`} sx={{ fontWeight: 700, ...(RISK_COLORS[afterTone] || RISK_COLORS.neutral) }} />
                      )}
                    </Stack>
                  </Stack>
                  {item.improve_id && (
                    <Box sx={{ mt: 1.2, p: 1.2, bgcolor: "#f8fafc", borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}>개선대책</Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 13.5, lineHeight: 1.55 }}>{item.improve_plan || "등록 내용 없음"}</Typography>
                      <Typography sx={{ mt: 0.65, color: "text.secondary", fontSize: 11.5 }}>
                        담당 {item.emp_name || "-"} · 예정 {item.expected_date || "-"} · 완료 {item.comp_date || "-"}
                      </Typography>
                    </Box>
                  )}
                  {(item.improve_before || item.improve_after) && (
                    <Box sx={{ mt: 1.2, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                      <EvidenceImage label="개선 전" src={item.improve_before} />
                      <EvidenceImage label="개선 후" src={item.improve_after} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
